import { Point } from "../models/Point";
import { EncodingUtils } from "../utils/EncodingUtils";
import { SvgUtils } from "../utils/SvgUtils";
import { VisualizerBase } from "./VisualizerBase";

export class Nervos extends VisualizerBase {
    readonly idTriangle: string;
    readonly idSquare: string;
    readonly idFiveStar: string;
    readonly idHexagram: string;

    constructor(_placeholderMode: boolean = false) {
        super("nervos", _placeholderMode);

        this.idTriangle = `triangle_${this.chainIdName}`;
        this.idSquare = `square_${this.chainIdName}`;
        this.idFiveStar = `fiveStar_${this.chainIdName}`;
        this.idHexagram = `hexagram_${this.chainIdName}`;
    }

    append4Shapes(defs: SVGDefsElement) {
        const triangleData = `M14.390998,5.0229993L9.5249989,13.579998 4.7309896,22.013998 14.400001,22.013998 24.049999,22.013998 19.280009,13.681999z M14.400001,0L21.600001,12.600998 28.800001,25.013998 14.400001,25.013998 0,25.013998 7.2000005,12.505999z`;
        const triangle = SvgUtils.createPath2(this.idTriangle, triangleData, true, `translate(10,13.62) scale(1.91)`, "", "", 0);
        defs.appendChild(triangle);

        const squareData = `M3,3L3,26 26,26 26,3z M0,0L29,0 29,29 0,29z`;
        const square = SvgUtils.createPath2(this.idSquare, squareData, true, `translate(10,10) scale(1.5)`, "", "", 0);
        defs.appendChild(square);

        const fiveStarData = `M15.997986,0L19.777008,11.620999 32,11.620999 22.109009,18.807002 25.888,30.432 15.997986,23.245997 6.1109924,30.432 9.8840027,18.807002 0,11.620999 12.222992,11.620999z`;
        const fiveStar = SvgUtils.createPath2(this.idFiveStar, fiveStarData, true, `translate(10,11.35) scale(1.7)`, "", "", 0);
        defs.appendChild(fiveStar);

        const hd1Data = `M57.3057 45.3442L60.4147 50.7299H32.5461L35.7331 56.2491H69.9765L60.4923 39.8238L57.3057 45.3442Z`;
        const hd1 = SvgUtils.createPath2("", hd1Data, true, ``, "", "", 0);
        const hd2Data = `M14.586 50.7299L28.519 26.5945H22.1436L5.02353 56.2491H23.9883L20.8043 50.7299H14.586Z`;
        const hd2 = SvgUtils.createPath2("", hd2Data, true, ``, "", "", 0);
        const hd3Data = `M37.5 11.0407L51.433 35.1762L54.6202 29.6546L37.5 0L28.0159 16.4253H34.3907L37.5 11.0407Z`;
        const hd3 = SvgUtils.createPath2("", hd3Data, true, ``, "", "", 0);
        const hd4Data = `M37.5 63.9593L23.567 39.8238L20.3804 45.3442L37.5 75L46.9816 58.5747H40.61L37.5 63.9593Z`;
        const hd4 = SvgUtils.createPath2("", hd4Data, true, ``, "", "", 0);
        const hd5Data = `M60.4147 24.2701L46.4811 48.4055H52.8533L69.9765 18.7487H51.0086L54.1963 24.2701H60.4147Z`;
        const hd5 = SvgUtils.createPath2("", hd5Data, true, ``, "", "", 0);
        const hd6Data = `M17.6955 29.6546L14.586 24.2701H42.4547L39.2669 18.7487H5.02353L14.5077 35.1762L17.6955 29.6546Z`;
        const hd6 = SvgUtils.createPath2("", hd6Data, true, ``, "", "", 0);
        const hexagram = document.createElementNS("http://www.w3.org/2000/svg", "g");
        hexagram.id = this.idHexagram;
        hexagram.appendChild(hd1);
        hexagram.appendChild(hd2);
        hexagram.appendChild(hd3);
        hexagram.appendChild(hd4);
        hexagram.appendChild(hd5);
        hexagram.appendChild(hd6);
        //hexagram.setAttribute("transform", `rotate(0,37.5,37.5)`);
        defs.appendChild(hexagram);
    }
    
    public override generate(hex: string, coreMode: boolean = false): SVGSVGElement {
        const svg = this.createSvg();
        //this.appendBlendStyle(svg);
        this.hexArray = EncodingUtils.hexStringToHexArray(hex);

        const size = 320;
        const center = size / 2;
        const R = 40;
        const r = 20;
        const r0 = 100;

        const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");

        const color1 = this.getColorHex(1, 2, 3);
        const color2 = this.getColorHex(4, 5, 6);
        const color3 = this.getColorHex(7, 8, 9);
        const idGradientBig1 = `gradient_big_1_${this.chainIdName}`;
        const idGradientBig2 = `gradient_big_2_${this.chainIdName}`;
        const idGradientBig3 = `gradient_big_3_${this.chainIdName}`;
        this.appendSimpleRadialGradient(defs, idGradientBig1, 0, -r0, R, color1, "0");
        this.appendSimpleRadialGradient(defs, idGradientBig2, 0, -r0, R, color2, "0");
        this.appendSimpleRadialGradient(defs, idGradientBig3, 0, -r0, R, color3, "0");
        
        const idOB = `circleOutbounding_${this.chainIdName}`;
        const dOB = SvgUtils.getDOfPathOfCircle(0, 0, 150);
        const obCircle = document.createElementNS("http://www.w3.org/2000/svg", "path");
        obCircle.id = idOB;
        obCircle.setAttribute("d", `${dOB}`);
        defs.appendChild(obCircle);

        this.append4Shapes(defs);
        svg.appendChild(defs);

        if(!coreMode) {
            // outbound: moving 3 circles
            this.appendMovingCirclesOutbound(svg, 150);
            // outbound: scaling circle
            this.appendDynamicScalingOutbound(svg, 1.4);
        }

        const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
        for(let row = 0; row < 4; row++){
            for(let col = 0; col < 4; col++){
                const index = row * 4 + col;
                const rotation = parseInt(this.hexArray[index], 16);
                const color = this.getColorHex(index+1, index+2, index+3);
                const posX = -150 + col * 75;
                const posY = -150 + row * 75;
                let idShape = "";
                let transform = `rotate(${rotation},${37.5+posX},${37.5+posY}) translate(${posX} ${posY})`;
                if(col === 0) idShape = this.idTriangle;
                else if(col === 1) idShape = this.idSquare;
                else if(col === 2) idShape = this.idFiveStar;
                else {
                    idShape = this.idHexagram;
                    transform = transform + " scale(0.7334)"; // no padding(10), so scale to (75-10x2)/75
                }
                this.addUsePartToGroup(group, idShape, transform, color, "", 0);
            }
        }

        group.setAttribute("transform", `translate(${center} ${center})`);//rotate(${angle},${center},${center}) 
        svg.appendChild(group);
        return svg;
    }

}